import { APP_URL, APP_TENANT } from "../constants/app-url"
import { getStoreRef } from "../store-ref"
import { getUUIDConversion, setUUIDConversion } from "./uuid"

const AUTH_IFRAME_TIMEOUT_MS = 5000

export const getFreshToken = (appTenant: string = APP_TENANT) => {
  // ROOT CAUSE (FPN-6442): appTenant defaults to APP_TENANT. Cookies are
  // named `${tenant}cookie.oidc.*`; every caller invoked this with no
  // argument, so the lookup was "undefinedcookie.oidc.id_token" — it never
  // matched and the scheduled refresh silently did nothing.
  const cookies = document.cookie.split(";")
  // startsWith prevents substring collisions across tenant prefixes
  // (e.g. "acme." matching "supportacme.cookie.oidc.id_token" during emulation)
  const idCookie = cookies.find((a) =>
    a.trim().startsWith(appTenant + "cookie.oidc.id_token=")
  )
  const hostname = APP_URL?.split(".")?.splice(1)?.join(".") || null
  const idTokenVal = idCookie?.split("=").slice(1).join("=") || ""
  if (idTokenVal !== null && idTokenVal !== undefined && idTokenVal !== "") {
    // Clear the old session cookies ONLY now that we know a refresh can
    // proceed. Deleting oidc.auth unconditionally (as before) wiped a live
    // session ~2 min before expiry even when the refresh then bailed (e.g.
    // id_token unreadable under blocked cross-site cookies), and could race
    // the idle-timeout SessionAlert's cookie check into a premature logout.
    // The iframe flow below restores both cookies.
    document.cookie = `${appTenant}cookie.oidc.auth=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT; Domain=${""}`
    document.cookie = `${appTenant}cookie.oidc.id_token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT; Domain=${""}`
    document.cookie = `${appTenant}cookie.oidc.id_token=${idTokenVal}; Path=/; Domain=${hostname}`

    // BUG #5: remove any auth iframe left over from a previous failed
    // attempt so retries can't pile hidden iframes up in the DOM
    document.querySelector(".authIframe")?.remove()

    const iframeElement = document.createElement("iframe")
    iframeElement.classList.add("authIframe")
    iframeElement.style.display = "none"
    iframeElement.src = `${APP_URL}/platform/login?page=${(
      window.location.pathname + window.location.hash
    ).slice(1)}`

    // BUG #5: the iframe must NOT be removed on `load` — the embedded page
    // posts NEW_TOKEN_RECEIVED AFTER load. Only clean up on error; the
    // no-completion case is handled by the FPN-6458 timeout below.
    iframeElement.onerror = () => {
      console.error("[getFreshToken] Auth iframe failed to load")
      iframeElement.remove()
      sessionStorage.removeItem("cookieCheck")
      sessionStorage.removeItem("authIframeTimeout")
    }

    document.body.append(iframeElement)
    sessionStorage.setItem("cookieCheck", "on")
    sessionStorage.setItem("authIframeTimeout", Date.now().toString())

    // FPN-6458: if the refresh doesn't complete within 5s (e.g. the browser
    // is blocking third-party cookies), remove the iframe and notify
    // listeners (navigationui listens for "authIframeTimeout").
    setTimeout(() => {
      const iframe = document.querySelector(".authIframe") as HTMLIFrameElement
      if (iframe && sessionStorage.getItem("cookieCheck") === "on") {
        console.warn("[coreui] Auth iframe timeout — cookies may be blocked by browser")
        iframe.remove()
        sessionStorage.removeItem("cookieCheck")
        sessionStorage.removeItem("authIframeTimeout")
        window.dispatchEvent(new CustomEvent("authIframeTimeout"))
      }
    }, AUTH_IFRAME_TIMEOUT_MS)
  } else {
    console.warn(
      "[getFreshToken] No id_token cookie found — cannot refresh token"
    )
  }
}

/**
 * Sets up a timeout to refresh the authentication token
 * @param timeInterval - Time interval in milliseconds
 * @returns Timer ID for potential cancellation
 */
export function runTimeoutForRefreshToken(
  timeInterval: number
): NodeJS.Timeout {
  const REFRESH_BUFFER_MS = 120000 // 2 minutes buffer before expiry
  console.log(`Setting token refresh timeout for ${timeInterval} ms`)
  if (timeInterval <= REFRESH_BUFFER_MS) {
    console.warn("Token refresh interval is too short, refreshing immediately")
    getFreshToken()
    return setTimeout(() => {}, 0)
  }

  return setTimeout(() => {
    getFreshToken()
  }, timeInterval - REFRESH_BUFFER_MS)
}

/**
 * To clear storage data
 * @param callLogoutUrl
 */
export const clearStorageData = (callLogoutUrl) => {
  const coreStore = getStoreRef()
  const isEmulated = sessionStorage.getItem("isEmulated")
  const oldAppTenant = sessionStorage.getItem("oldAppTenant")
  const cookies = document.cookie.split(";")
  const idCookie = cookies.find((a) =>
    a.trim().startsWith(
      (isEmulated === null ? APP_TENANT : oldAppTenant) +
        "cookie.oidc.id_token="
    )
  )
  const idTokenVal = idCookie?.split("=").slice(1).join("=") || ""
  document.cookie = `${APP_TENANT}cookie.oidc.id_token=; Expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; Domain=${""}`
  document.cookie = `${APP_TENANT}cookie.oidc.auth=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT; Domain=${""}`
  sessionStorage.removeItem("iframeUrl")
  // "Do not show me this message again" (idle-timeout warning) must not
  // outlive the session: nothing else clears it, so one tick of the
  // checkbox permanently disabled idle auto-logout for every future user
  // of this browser profile.
  localStorage.removeItem("persistSession")
  if (isEmulated) {
    document.cookie = `${oldAppTenant}cookie.oidc.id_token=; Expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; Domain=${""}`
    document.cookie = `${oldAppTenant}cookie.oidc.auth=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT; Domain=${""}`
    sessionStorage.removeItem("oldAppTenant")
    sessionStorage.removeItem("isEmulated")
    sessionStorage.removeItem("emulatedURL")
    sessionStorage.removeItem("oldCookies")
    sessionStorage.removeItem("oldUserInfo")
    sessionStorage.removeItem("editProfilePrevUrl")
  }
  if (callLogoutUrl) {
    // `||` not `??`: idTokenVal is coerced to "" above (never nullish), so
    // with ?? the store fallback was dead code and logout silently degraded
    // to a login redirect whenever the id_token cookie was missing.
    let tokenHint = idTokenVal || coreStore.getState()?.idTokenEncoded
    if (tokenHint) {
      window.location.href = `/platform/logout?id_token_hint=${tokenHint}`
    } else {
      const hashPath: any = String(
        window.location.pathname + window.location.hash
      )
      window.location.href = `/platform/login?page=${hashPath.slice(1)}`
    }
  }
}

interface LogoutResult {
  appId: string
  appName?: string
  success: boolean
  error?: string
}

export const logoutFromIframes = async (): Promise<LogoutResult[]> => {
  const coreStore = getStoreRef()
  const state = coreStore.getState()
  const applications = state.applications || []
  const logoutApps = applications.filter((app) => app.iframeLogoutURL)

  if (logoutApps.length === 0) {
    return []
  }

  const results = await Promise.allSettled(
    logoutApps.map((app) => logoutSingleApp(app))
  )

  const logoutResults: LogoutResult[] = results.map((result, index) => {
    const app = logoutApps[index]
    if (result.status === "fulfilled") {
      return {
        appId: app.id || `app-${index}`,
        appName: app.applicationName,
        success: true
      }
    } else {
      return {
        appId: app.id || `app-${index}`,
        appName: app.applicationName,
        success: false,
        error: result.reason?.message || "Unknown error"
      }
    }
  })

  return logoutResults
}

const logoutSingleApp = (app: any): Promise<void> => {
  return new Promise((resolve, reject) => {
    let tempIframeLogoutURL = app.iframeLogoutURL
    try {
      // If an iframeLogoutURL is saved as a stringified object, it is parsed here and we select the iframe Logout URL based on the appropriate region.
      // Currently only eu and us mapping is allowed, based on browser URL
      const parsedUrl = JSON.parse(tempIframeLogoutURL)
      if (typeof parsedUrl === "object" && parsedUrl !== null) {
        const coreStore = getStoreRef()
        const storeData = coreStore.getState()
        const userInfoTenants = storeData?.userInfo?.ext?.tenantId || []
        const sseID = userInfoTenants.find((a) => a.hint === "SSE")
        const fwID = userInfoTenants.find((a) => a.hint === "FW")
        const data = setUUIDConversion(fwID.id, sseID.id)
        const conv = getUUIDConversion(data)
        const region =
          conv.includes("203_") || storeData.useSSEEUURL ? "eu" : "us"
        tempIframeLogoutURL = parsedUrl[region]
      }
    } catch (e) {
      // If parsing fails, keep the original string value
    }

    // Validate URL
    try {
      new URL(tempIframeLogoutURL)
    } catch (e) {
      reject(new Error(`Invalid logout URL: ${tempIframeLogoutURL}`))
      return
    }

    const iframeElement = document.createElement("iframe")
    iframeElement.classList.add("logoutIframe")
    iframeElement.style.display = "none"
    iframeElement.setAttribute(
      "sandbox",
      "allow-same-origin allow-scripts allow-forms"
    )

    iframeElement.src = tempIframeLogoutURL

    let isResolved = false

    const cleanup = () => {
      if (document.body.contains(iframeElement)) {
        document.body.removeChild(iframeElement)
      }
    }

    const timeout = setTimeout(() => {
      if (!isResolved) {
        isResolved = true
        console.warn(`Logout timeout for ${app.applicationName || app.id}`)
        cleanup()
        resolve() // Don't reject on timeout, just resolve
      }
    }, 8000) // 8 second timeout

    iframeElement.onload = () => {
      if (!isResolved) {
        // Wait a bit for the logout to process
        setTimeout(() => {
          if (!isResolved) {
            isResolved = true
            clearTimeout(timeout)
            cleanup()
            resolve()
          }
        }, 1000)
      }
    }

    iframeElement.onerror = () => {
      if (!isResolved) {
        isResolved = true
        console.warn(`Logout failed for ${app.applicationName || app.id}`)
        clearTimeout(timeout)
        cleanup()
        resolve() // Don't fail the entire logout process
      }
    }

    try {
      document.body.appendChild(iframeElement)
    } catch (e) {
      isResolved = true
      clearTimeout(timeout)
      reject(new Error(`Failed to create iframe: ${e.message}`))
    }
  })
}
